import * as React from 'react';
import Box from '@mui/material/Box';
import { CustomerImageList } from './CustomerImageList';
import { CompareOverlay } from '../../../shared/compareImages/view/CompareOverlay';
import CreateStitchedImageDialog from '../../imageStitching/view/CreateStitchedImageDialog';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { useCompareSelection } from '../../compareImages/state/use-compare-selection';
import { useImageCompare } from '../../../shared/compareImages/state/use-image-compare';
import { useImageStitching } from '../../imageStitching/state/use-image-stitching';
import { IImageThumbnail } from '../../../Entities';
import * as Features from '../../../../../../../shared/Features';
import * as PhotoStatus from '../../../../../../../shared/PhotoStatus';
import { ImagesActionBox } from './ImagesActionBox';
import { transformExtraImageCost } from '../../approval/state/utils';
import { IExtraImageCost } from '../queries/use-query-images';
import { IOrderRowQuantitiesInfo } from '../../entry/queries/use-query-photo-status';

type ImagesViewProps = {
    images: IImageThumbnail[];
    user: AuthenticatedUser;
    orderId: number;
    productName: string;
    numberOfImages: number;
    productProperties: any[];
    photoStatus: number;
    getEffectiveCategory: (image: IImageThumbnail) => number;
    toggleCategory: (imageId: number) => void;
    deliveredCount: number;
    nonStitchedDeliveredCount: number;
    orderedCount: number;
    orderRowQuantitiesInfo: IOrderRowQuantitiesInfo[];
    extraImageCost: IExtraImageCost | null;
    isApprovalInProgress: boolean;
};

export function ImagesView({
    images,
    user,
    orderId,
    productName,
    numberOfImages,
    productProperties,
    photoStatus,
    getEffectiveCategory,
    toggleCategory,
    deliveredCount,
    nonStitchedDeliveredCount,
    orderedCount,
    orderRowQuantitiesInfo,
    extraImageCost,
    isApprovalInProgress
}: ImagesViewProps) {
    const {
        isSelecting,
        selectedImageIds,
        isSelectionFull,
        startSelection,
        cancelSelection,
        toggleImageSelection
    } = useCompareSelection();
    const { isComparing, openCompare, closeCompare } = useImageCompare();
    const {
        isStitching,
        isStitchReady,
        stitchDialogOpen,
        stitchImages,
        stitchFormRef,
        selectedStitchHeight,
        startStitching,
        cancelStitching,
        closeStitchDialog,
        handleStitchCreated,
        handleStitchFormChange,
        handleStitchSubmit
    } = useImageStitching({ images });

    const allowStitching =
        photoStatus >= PhotoStatus.CustomerApproved && Features.hasFeature(Features.CreateStitchedImage, user);

    const handleCancelSelection = React.useCallback(() => {
        cancelSelection();
        closeCompare();
    }, [cancelSelection, closeCompare]);

    return (
        <Box component="section">
            <Box
                component="form"
                ref={stitchFormRef}
                onSubmit={handleStitchSubmit}
                onChange={handleStitchFormChange}
            >
                <ImagesActionBox
                    nonStitchedDeliveredCount={nonStitchedDeliveredCount}
                    productName={productName}
                    numberOfImages={numberOfImages}
                    productProperties={productProperties}
                    photoStatus={photoStatus}
                    isSelecting={isSelecting}
                    selectedImageIds={selectedImageIds}
                    onSelectStart={startSelection}
                    onCancelSelection={handleCancelSelection}
                    onCompare={openCompare}
                    allowStitching={allowStitching}
                    isStitching={isStitching}
                    isStitchReady={isStitchReady}
                    onStitchToggle={startStitching}
                    onStitchCancel={cancelStitching}
                    orderRowQuantitiesInfo={orderRowQuantitiesInfo}
                    isApprovalInProgress={isApprovalInProgress}
                />
                <CustomerImageList
                    user={user}
                    images={images}
                    orderId={orderId}
                    isSelecting={isSelecting}
                    selectedImageIds={selectedImageIds}
                    isSelectionFull={isSelectionFull}
                    onToggleSelect={toggleImageSelection}
                    getEffectiveCategory={getEffectiveCategory}
                    onToggleCategory={toggleCategory}
                    deliveredCount={deliveredCount}
                    orderedCount={orderedCount}
                    photoStatus={photoStatus}
                    isStitching={isStitching}
                    selectedStitchHeight={selectedStitchHeight}
                    extraImageCost={transformExtraImageCost(extraImageCost)}
                    isApprovalInProgress={isApprovalInProgress}
                />
            </Box>
            <CompareOverlay
                open={isComparing}
                onClose={closeCompare}
                images={images}
                selectedImageIds={selectedImageIds}
            />
            <CreateStitchedImageDialog
                open={stitchDialogOpen}
                onClose={closeStitchDialog}
                leftImage={stitchImages.left}
                rightImage={stitchImages.right}
                orderId={orderId}
                onCreated={handleStitchCreated}
            />
        </Box>
    );
}
